import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { tailwindConfig } from 'tailwind-config';
import { useAccount, useChainId } from 'wagmi';

import { PairWalletsContext } from 'context/PairWalletsContext/PairWalletsContext';
import { TransfersCenter } from 'features/transfer/ui/TransfersCenter';
import { useTypedSelector } from 'store';
import { TableAction } from 'ui/Table';
import { TableActionIcon } from 'ui/Table';
import { Tooltip } from 'ui/Tooltip';
import { chains } from 'web3';

import { WalletSync } from './WalletSync';
import { WithdrawWhitelistButton } from './WithdrawalWhitelistButton';

import { ArrowFromIcon, ArrowLeftRight, ArrowToIcon, ExitIcon, IconChecklist } from 'assets/icons';

const Actions = () => {
  const dexPair = useTypedSelector(store => store.pairs.selectedDexPair)!;
  const isAdmin = useTypedSelector(store => store.auth.isAdmin);

  const [isVisibleModalWalletSync, setIsVisibleModalWalletSync] = useState(false);
  const [isTransfersCenterOpen, setIsTransfersCenterOpen] = useState(false);
  const [isTransferMenuOpen, setIsTransferMenuOpen] = useState(false);
  const transferDropdownRef = useRef<HTMLDivElement>(null);
  const {
    selectedRecords,
    modals: {
      changeNetworkOpened,
      depositOpened,
      withdrawOpened,
      transferOpened,
      disconnectWalletsOpened,
      connectWalletsOpened,
      importWalletsOpened,
    },
  } = useContext(PairWalletsContext);
  const { address } = useAccount();
  const chainId = useChainId();

  const metamaskConnectionIsValid = useMemo<boolean>(() => {
    if (!address || !dexPair) return false;

    for (const network of Object.keys(chains)) {
      if (chainId && dexPair.network === network && chainId !== chains[network]) return false;
    }

    return true;
  }, [address, dexPair, chainId]);

  const handleOpenDepositModal = useCallback(() => {
    if (!metamaskConnectionIsValid) {
      changeNetworkOpened.set(true);
      return;
    }

    depositOpened.set(true);
  }, [metamaskConnectionIsValid, changeNetworkOpened, depositOpened]);

  const handleOpenWithdrawModal = useCallback(() => {
    withdrawOpened.set(true);
  }, [withdrawOpened]);

  const handleOpenTransferModal = useCallback(() => {
    transferOpened.set(true);
  }, [transferOpened]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isTransferMenuOpen &&
        transferDropdownRef.current &&
        !transferDropdownRef.current.contains(event.target as Node)
      ) {
        setIsTransferMenuOpen(false);
      }
    };

    window.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isTransferMenuOpen]);

  const handleOpenTransferFromMenu = useCallback(() => {
    if (selectedRecords.length !== 1) return;
    handleOpenTransferModal();
    setIsTransferMenuOpen(false);
  }, [handleOpenTransferModal, selectedRecords.length]);

  const handleOpenTransfersCenter = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setIsTransfersCenterOpen(true);
    setIsTransferMenuOpen(false);
  }, []);

  const handleDeactivateWallets = useCallback(() => {
    disconnectWalletsOpened.set(true);
  }, [disconnectWalletsOpened]);

  return isAdmin ? (
    <>
      {isVisibleModalWalletSync && (
        <WalletSync onClose={() => setIsVisibleModalWalletSync(false)} />
      )}
      {isTransfersCenterOpen && (
        <TransfersCenter pair={dexPair} onClose={() => setIsTransfersCenterOpen(false)} />
      )}

      <TableAction text="Connect" onClick={() => connectWalletsOpened.set(true)} />
      <TableAction text="Import" onClick={() => importWalletsOpened.set(true)} />
      <TableAction
        iconLeft={<TableActionIcon icon={ArrowToIcon} disabled={selectedRecords.length === 0} />}
        text="Deposit"
        disabled={selectedRecords.length === 0}
        onClick={handleOpenDepositModal}
      />

      <TableAction
        text="Transfer"
        onClick={handleOpenTransferFromMenu}
        disabled={selectedRecords.length !== 1}
        iconLeft={
          selectedRecords.length > 1 ? (
            <Tooltip
              trigger={
                <TableActionIcon
                  icon={() => <ArrowLeftRight className="w-[16px] h-[16px] cursor-not-allowed" />}
                  disabled={selectedRecords.length !== 1}
                />
              }
              options={{
                placement: undefined,
                offset: 0,
              }}
              content="You can start the transfer only after selecting one account."
            />
          ) : (
            <TableActionIcon
              icon={() => <ArrowLeftRight className="w-[16px] h-[16px]" />}
              disabled={selectedRecords.length !== 1}
            />
          )
        }
        iconRight={
          <div onClick={handleOpenTransfersCenter} className="cursor-pointer">
            <IconChecklist
              color={tailwindConfig.theme.colors.primary}
              className="w-[20px] h-[20px]"
            />
          </div>
        }
      />

      <TableAction text="Sync" onClick={() => setIsVisibleModalWalletSync(true)} />

      <TableAction
        iconLeft={<TableActionIcon icon={ArrowFromIcon} disabled={selectedRecords.length === 0} />}
        iconRight={<WithdrawWhitelistButton />}
        text="Withdraw"
        disabled={selectedRecords.length === 0}
        onClick={handleOpenWithdrawModal}
      />
      <TableAction
        iconLeft={<TableActionIcon icon={ExitIcon} disabled={selectedRecords.length === 0} />}
        text="Deactivate"
        disabled={selectedRecords.length === 0}
        onClick={handleDeactivateWallets}
      />
    </>
  ) : null;
};

export default Actions;
